import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Card, Button, Input, ClickableCard } from '../components/common';
import { presetService } from '../services/presetService';
import { Preset, PresetListResponse } from '../types/preset';

const PresetPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const SearchContainer = styled.div`
  width: 100%;
  max-width: 400px;
  margin-bottom: 1.5rem;
`;

const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
`;

const PresetDetail = styled.div`
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const PresetLabel = styled.span`
  font-weight: 500;
  color: #4b5563;
  margin-right: 0.5rem;
`;

const PresetValue = styled.span`
  color: #1f2937;
`;

const PresetDescription = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0.5rem 0 1rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled(Button)`
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  gap: 0.5rem;
`;

const PaginationButton = styled(Button)`
  min-width: 40px;
`;

const PresetsPage: React.FC = () => {
  const navigate = useNavigate();
  const [presets, setPresets] = useState<Preset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;
  const [deletingPresetId, setDeletingPresetId] = useState<string | null>(null);

  useEffect(() => {
    fetchPresets();
  }, [page, searchQuery]);

  const fetchPresets = async () => {
    try {
      setLoading(true);
      const response: PresetListResponse = await presetService.getPresets(page, pageSize, searchQuery);
      setPresets(response.items);
      setTotalPages(Math.ceil(response.total / pageSize));
      setError(null);
    } catch (err) {
      console.error('Error fetching presets:', err);
      setError('Failed to load presets. Please try again later.');
      setPresets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };
  
  const handleCreatePreset = () => {
    navigate('/presets/create');
  };
  
  const handlePresetClick = (presetId: string) => {
    navigate(`/admin/presets/${presetId}`);
  };

  const handleEditPreset = (presetId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    navigate(`/admin/presets/${presetId}/edit`);
  };

  const handleDeletePreset = async (presetId: string, presetName: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    
    if (!window.confirm(`Are you sure you want to delete preset "${presetName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingPresetId(presetId);
      await presetService.deletePreset(presetId);
      
      // Refresh the presets list
      await fetchPresets();
      
      // Show success message (you can use toast here if available)
      alert(`Preset "${presetName}" deleted successfully.`);
    } catch (err) {
      console.error('Error deleting preset:', err);
      alert(`Failed to delete preset "${presetName}". Please try again.`);
    } finally {
      setDeletingPresetId(null);
    }
  };

  const renderPagination = () => {
    return (
      <PaginationContainer>
        <PaginationButton 
          onClick={() => handlePageChange(page - 1)} 
          disabled={page === 1}
          variant="secondary"
          size="small"
        >
          Prev
        </PaginationButton>
        
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          const pageNum = page > 3 ? page - 3 + i + 1 : i + 1;
          if (pageNum <= totalPages) {
            return (
              <PaginationButton
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                variant={pageNum === page ? 'primary' : 'secondary'}
                size="small"
              >
                {pageNum}
              </PaginationButton>
            );
          }
          return null;
        })}
        
        <PaginationButton
          onClick={() => handlePageChange(page + 1)}
          disabled={page === totalPages}
          variant="secondary"
          size="small"
        >
          Next
        </PaginationButton>
      </PaginationContainer>
    );
  };

  return (
    <PresetPageContainer>
      <HeaderSection>
        <PageTitle>Trading Presets</PageTitle>
      </HeaderSection>
      
      <SearchContainer>
        <Input 
          placeholder="Search presets..." 
          value={searchQuery}
          onChange={handleSearch}
          fullWidth
        />
      </SearchContainer>
      
      {loading ? (
        <p>Loading presets...</p>
      ) : error ? (
        <Card>
          <p>{error}</p>
        </Card>
      ) : presets.length === 0 ? (
        <Card>
          <p>No presets found. Create a new preset to get started.</p>
          <CardFooter>
            <Button onClick={handleCreatePreset}>Create Preset</Button>
          </CardFooter>
        </Card>
      ) : (
        <>
          <PresetGrid>
            {presets.map((preset) => (
              <ClickableCard 
                key={preset.id} 
                title={preset.name}
                onClick={() => handlePresetClick(preset.id)}
              >
                <PresetDescription>{preset.description}</PresetDescription>
                <PresetDetail>
                  <PresetLabel>Symbol:</PresetLabel>
                  <PresetValue>{preset.symbol}</PresetValue>
                </PresetDetail>
                <PresetDetail>
                  <PresetLabel>Side:</PresetLabel>
                  <PresetValue>{preset.side}</PresetValue>
                </PresetDetail>
                <CardFooter>
                  <div>
                    <PresetLabel>Type:</PresetLabel>
                    <PresetValue>{preset.order_type}</PresetValue>
                  </div>
                  <CardActions>
                    <ActionButton 
                      variant="secondary" 
                      size="small"
                      onClick={(e) => handleEditPreset(preset.id, e)}
                    >
                      Edit
                    </ActionButton>
                    <ActionButton 
                      variant="danger" 
                      size="small"
                      onClick={(e) => handleDeletePreset(preset.id, preset.name, e)}
                      disabled={deletingPresetId === preset.id}
                    >
                      {deletingPresetId === preset.id ? 'Deleting...' : 'Delete'}
                    </ActionButton>
                  </CardActions>
                </CardFooter>
              </ClickableCard>
            ))}
          </PresetGrid>
          
          {renderPagination()}
        </>
      )}
    </PresetPageContainer>
  );
};

export default PresetsPage; 